import type { CityCode } from '@loci/city-editions';

import { appUrl, type CrossHostStageOptions } from '@/lib/cross-host-urls';

/**
 * Host surface that owns the current page. Mirrors `CitySurfaceLabel` without
 * importing the server-only `city-context` module — this helper runs in client
 * components (`useCitySurface()`) as well as server builders.
 *
 * Canonical home for the union: `lib/recommendations/eventHref.ts` aliases it
 * so the two surface-aware href builders cannot drift apart.
 */
export type HrefSurface = 'marketing-apex' | 'marketing-city' | 'app' | 'fallback';

/**
 * Single source of truth for links to **city-scoped app-surface** pages
 * (`app.loci.city/<city>/…` — settings, notifications, saved, profile).
 *
 * The mirror image of {@link import('./recommendations/eventHref').buildEventDetailHref}:
 * that one owns `/event/*`, which lives only on the marketing-city host; this
 * one owns `/<city>/*`, which lives only on the app host.
 *
 * - `marketing-city` / `marketing-apex`: cross to the app host via `appUrl()`.
 *   A root-relative `/<city>/…` href does **not** resolve on a marketing host:
 *   `applySurfaceRouting` (proxy.ts) 307s any marketing request whose first
 *   path segment is a registered city code onto the app surface. Next treats
 *   the root-relative form as an internal route and RSC-prefetches it, the
 *   proxy redirects that prefetch cross-host, and `connect-src` — which lists
 *   neither `app.loci.city` nor `staging.app.loci.city` — blocks the redirected
 *   response (WEB-834). Widening the CSP is not the fix: Next cannot RSC-prefetch
 *   cross-origin usefully anyway, so that would legalise a request that should
 *   never be made.
 * - `app`: same-origin relative path, so the App Router keeps the soft
 *   navigation (and its prefetch) on the surface that owns the page.
 * - `fallback` (localhost / `*.vercel.app`): single-host, and `applySurfaceGuard`
 *   skips it entirely — stay relative so local dev and preview deploys resolve
 *   on the origin actually serving them.
 *
 * `stage` must be threaded from request context (`useCityIsStaging()` →
 * `x-is-staging`), never hardcoded: `NEXT_PUBLIC_LOCI_STAGE` is inlined at
 * build time and an unset staging env would emit production `app.loci.city`
 * from `staging-clt.loci.city` (WEB-776).
 */
export function buildAppSurfaceHref(
  cityCode: CityCode,
  path: string,
  surface: HrefSurface = 'app',
  stage?: CrossHostStageOptions,
): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const crossesHost = surface === 'marketing-city' || surface === 'marketing-apex';
  return crossesHost
    ? appUrl({ city: cityCode, path: normalized, ...stage })
    : `/${cityCode.toLowerCase()}${normalized}`;
}

/**
 * True when `href` is an absolute URL (i.e. it crossed hosts). Call sites use
 * this to render a native `<a>` instead of `next/link`: there is no
 * client-side route to push across origins and nothing the App Router can
 * usefully RSC-prefetch, so keeping "no `?_rsc=` request" structural beats a
 * `prefetch={false}` prop a later refactor could quietly drop.
 */
export function isCrossHostHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}
